
import { useQuery } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { format } from "date-fns";
import { pt } from "date-fns/locale";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { ExerciseImage } from "@/components/exercises/ExerciseImage";
import { User, UserCog } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { useLanguagePath } from "@/utils/language";
import { useTranslations } from "@/hooks/useTranslations";

export const TrainingSessionDetails = () => {
  const { id } = useParams();
  const { getPath } = useLanguagePath();
  const { t } = useTranslations("training-sessions");

  const { data: session, isLoading } = useQuery({
    queryKey: ["training-session", id],
    queryFn: async () => {
      // First get custom exercises to check against
      const { data: customExercises, error: customExerciseError } = await supabase
        .from("custom_exercises")
        .select("name, trainer_id");

      if (customExerciseError) {
        console.error("Erro ao buscar exercícios personalizados:", customExerciseError);
      }

      const customExerciseNames = customExercises?.map(ce => ce.name.toLowerCase()) || [];

      // Fetch session with all related data including exercises
      const { data: sessionData, error: sessionError } = await supabase
        .from("training_sessions")
        .select(`
          *,
          student:students(first_name, last_name),
          workout:workouts(
            id,
            title,
            description,
            exercises(
              id,
              name,
              sets,
              reps,
              weight,
              notes,
              image_url,
              order_index
            )
          ),
          trainer:profiles!training_sessions_trainer_id_fkey(id)
        `)
        .eq("id", id)
        .single();

      if (sessionError) throw sessionError;
      if (!sessionData) throw new Error("Session not found");

      // Mark custom exercises
      if (sessionData.workout?.exercises) {
        sessionData.workout.exercises = sessionData.workout.exercises.map(exercise => ({
          ...exercise,
          isCustom: customExerciseNames.includes(exercise.name?.toLowerCase() || '')
        }));
      }

      // Get completed sets for each exercise
      const { data: sessionExercises } = await supabase
        .from("training_session_exercises")
        .select("*")
        .eq("training_session_id", id);

      // Combine workout exercises with session exercise data
      const exercises = sessionData.workout?.exercises?.map(exercise => {
        const sessionExercise = sessionExercises?.find(se => se.exercise_id === exercise.id);
        return {
          ...exercise,
          sets_completed: sessionExercise?.sets_completed || 0,
          weight: sessionExercise?.weight || exercise.weight,
          notes: sessionExercise?.notes || exercise.notes
        };
      }) || [];

      return {
        ...sessionData,
        exercises
      };
    },
  });

  if (isLoading) {
    return <Skeleton className="w-full h-48" />;
  }

  if (!session) {
    return <div>Aula não encontrada</div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-3xl font-bold tracking-tight gradient-text text-slate-950 dark:text-white mb-2">
          {t("title")}
        </h2>
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink href={getPath("/dashboard")} className="text-flame hover:text-flame/80">
                {t("breadcrumbs.dashboard")}
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbLink href={getPath("/dashboard/training-sessions")} className="text-flame hover:text-flame/80">
                {t("breadcrumbs.sessions")}
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>{t("table.date")}</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
      </div>

      <div className="grid gap-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Informações da Aula</CardTitle>
            <Badge 
              variant={session.created_by_type === 'student' ? "warning" : "default"}
              className="cursor-help"
            >
              {session.created_by_type === 'student' ? (
                <User className="h-3 w-3 mr-1" />
              ) : (
                <UserCog className="h-3 w-3 mr-1" />
              )}
              {session.created_by_type === 'student' ? "Registada pelo Aluno" : "Registada pelo Personal"}
            </Badge>
          </CardHeader>
          <CardContent>
            <dl className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <dt className="font-medium">Data</dt>
                <dd className="text-gray-600">
                  {format(new Date(session.created_at), "PPP", { locale: pt })}
                </dd>
              </div>
              <div>
                <dt className="font-medium">Aluno</dt>
                <dd className="text-gray-600">
                  {session.student.first_name} {session.student.last_name}
                </dd>
              </div>
              <div>
                <dt className="font-medium">Treino</dt>
                <dd className="text-gray-600">{session.workout.title}</dd>
              </div>
              <div>
                <dt className="font-medium">Dia</dt>
                <dd className="text-gray-600">{session.workout_day}</dd>
              </div>
              {session.notes && (
                <div className="col-span-2">
                  <dt className="font-medium">Notas</dt>
                  <dd className="text-gray-600">{session.notes}</dd>
                </div>
              )}
            </dl>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Exercícios</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              {session.exercises.map((sessionExercise: any) => (
                <div key={sessionExercise.id} className="border-b pb-4 last:border-0">
                  <div className="flex items-start gap-4 mb-3">
                    <div className="w-16 h-16 rounded-lg overflow-hidden border-2 border-slate-100 shadow-sm">
                      <ExerciseImage
                        imagePath={sessionExercise.image_url || ""}
                        altText={sessionExercise.name}
                        fallbackText={sessionExercise.name}
                        className="w-full h-full object-cover"
                        isCustomExercise={sessionExercise.isCustom}
                      />
                    </div>
                    <div>
                      <h4 className="font-medium">{sessionExercise.name}</h4>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-2">
                        <div>
                          <dt className="text-sm text-gray-500">Séries</dt>
                          <dd>{sessionExercise.sets_completed} / {sessionExercise.sets}</dd>
                        </div>
                        <div>
                          <dt className="text-sm text-gray-500">Repetições</dt>
                          <dd>{sessionExercise.reps}</dd>
                        </div>
                        {sessionExercise.weight && (
                          <div>
                            <dt className="text-sm text-gray-500">Peso</dt>
                            <dd>{sessionExercise.weight}</dd>
                          </div>
                        )}
                        {sessionExercise.notes && (
                          <div>
                            <dt className="text-sm text-gray-500">Notas</dt>
                            <dd>{sessionExercise.notes}</dd>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
